<script setup lang="ts">
    import { computed } from 'vue';
    import { signInAgainTarget } from '@/shared/signInFailed.ts';

    type Props = {
        page?: string;
    };

    const props = withDefaults(defineProps<Props>(), {
        page: ''
    });

    const helpUrl = computed(() => window.config?.reportErrorUrl);

    /**
     * MSAL keeps its cache per tab in `sessionStorage`, so a tab whose sign-in has failed keeps failing until the
     * cache is cleared. Clearing it and loading the requested page again gives a fresh sign-in, the same way a new
     * tab does.
     */
    function signInAgain() {
        sessionStorage.clear();
        window.location.assign(signInAgainTarget(props.page));
    }
</script>

<template>
    <section class="section">
        <div class="sign-in-failed-card has-text-centered">
            <b-icon
                size="is-large"
                icon="account-alert"
                class="mb-4" />
            <h1 class="title is-4">
                Sign-in could not be completed
            </h1>
            <p class="mb-5">
                Common Library could not confirm who you are, so the page could not be shown.
                This is not a problem with your access. Signing in again normally fixes it.
            </p>
            <b-button
                type="is-primary"
                icon-left="login"
                @click="signInAgain">
                Sign in again
            </b-button>
            <p
                v-if="helpUrl"
                class="mt-5">
                If this keeps happening, <a
                    :href="helpUrl"
                    target="_blank">request help in ServiceNow</a>.
            </p>
        </div>
    </section>
</template>

<style scoped>
.sign-in-failed-card {
    max-width: 40rem;
    margin: 0 auto;
    padding: 2rem;
    border-radius: 0.5rem;
    background-color: var(--eq-bg-highlight);
}
</style>
